import { FC, useCallback, useMemo, useState, useRef } from "react";
import { useTranslation } from "next-i18next";
import {
  Filters as FiltersUI,
  FiltersWording,
  FilterItem,
  FiltersOutput,
} from "@business-finland/wif-ui-lib/dist/Filters";
import { Pagination } from "@business-finland/wif-ui-lib/dist/Pagination";

import { ServicesList } from "./ServicesList";
import { useServicesQuery } from "./useServicesQuery";
import { useServicesListPagination } from "./useServicesListPagination";

export type ServicesListWithFiltersProps = {
  categories: Wif.Mgnl.Node<Wif.Mgnl.Category>[];
  displayOnlyChildrenPages?: boolean;
  currentPagePath?: string;
  wording?: FiltersWording;
};

export const ServicesListWithFilters: FC<ServicesListWithFiltersProps> = (
  props
) => {
  const { categories, displayOnlyChildrenPages, currentPagePath, wording } =
    props;
  const { t } = useTranslation("common");
  const ref = useRef<HTMLDivElement>(null);

  // Use pagination hook
  const { page, limit, handlePageChange, resetToFirstPage } =
    useServicesListPagination({
      limit: 4,
    });

  const initialFiltersValues: FiltersOutput = useMemo(
    () => ({
      categories: categories.map((category) => category["@id"]).join(","),
    }),
    [categories]
  );
  const [filtersValues, setFiltersValues] =
    useState<FiltersOutput>(initialFiltersValues);

  const filtersChangeHandler = useCallback(
    (values: FiltersOutput) => {
      setFiltersValues({
        ...values,
        categories:
          // no categories selected - behave as all are selected
          !values.categories
            ? initialFiltersValues.categories
            : values.categories,
      });
      // Reset to first page when filters change
      resetToFirstPage();
    },
    [initialFiltersValues, resetToFirstPage]
  );

  // Page change handler with scrolling
  const handlePageChangeWithScroll = useCallback(
    (newPage: number) => {
      handlePageChange(newPage);

      // Add a small delay to prevent glitching, then scroll to top of component
      setTimeout(() => {
        if (typeof window !== "undefined" && ref.current) {
          const top = ref.current.offsetTop;
          window.scrollTo({ top: top - 80, behavior: "smooth" });
        }
      }, 100);
    },
    [handlePageChange]
  );

  const { services, pageCount } = useServicesQuery(
    filtersValues?.categories ? filtersValues : initialFiltersValues,
    page,
    limit,
    displayOnlyChildrenPages,
    currentPagePath
  );

  const categoriesForFilters: FilterItem[] = useMemo(
    () =>
      categories.map((category) => ({
        value: category["@id"],
        label: category.displayName,
      })),
    [categories]
  );

  return (
    <div ref={ref}>
      <section className="Filters">
        <FiltersUI
          filters={{
            name: "categories",
            items: categoriesForFilters,
            isMulti: true,
          }}
          changeHandler={filtersChangeHandler}
          wording={wording}
        />
      </section>
      <section className="ServicesList">
        <ServicesList services={services} />
      </section>

      {pageCount > 1 && (
        <Pagination
          count={pageCount}
          value={page}
          onChange={handlePageChangeWithScroll}
          i18n={{
            btnNextText: t("pagination.nextPage"),
            btnPageText: t("pagination.goToPage"),
            btnPrevText: t("pagination.previousPage"),
          }}
        />
      )}
    </div>
  );
};
